import PropTypes from "prop-types";
import React, { useEffect, useState, Fragment } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import { fetchPendingClaimsReport, deleteClaim } from "actions/claimActions";
import { Card, CardSection, Button, Loader } from "cxa-components";
import { FileDownloadIcon } from "cxa-icons";
import { injectIntl, FormattedMessage, intlShape } from "react-intl";
import messages from "components/messagesDescription";
import { PendingClaimsTable, DeleteClaimDialog } from "./widgets";
import { showEstimatedDateMessage } from "./util";
import {
  getClaimDashboard,
  getDelegatedEmployees,
  getInsurerClaimCategories,
} from "../../actions/claimActionsPP";
import { getShortLivedToken } from "actions/loginActions";

const TrackClaimsPage = ({ intl, history }) => {
  const data = useSelector(state => state.claimsPP);

  const { claimDashboard, selectedDelegatedEmployeeKey } = data;
  const defaultPlanYear = claimDashboard && claimDashboard.plans && claimDashboard.plans[0].planID;

  const [selectedPlanYear, setSelectedPlanYear] = useState(null);
  const [modalFlag, setModalFlag] = useState(false);
  const [selectedClaimId, setSelectedClaimId] = useState(null);
  const [selectedClaimObjectId, setSelectedClaimObjectId] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState("All");
  const [selectedDelegator, setSelectedDelegator] = useState(
    selectedDelegatedEmployeeKey !== null ? selectedDelegatedEmployeeKey : "me"
  );
  const dispatch = useDispatch();

  useEffect(() => {
    if(selectedDelegator !== "me"){
      dispatch(getClaimDashboard(selectedPlanYear, selectedDelegator));
    } else {
      dispatch(getClaimDashboard());
    }
    dispatch(getInsurerClaimCategories());
    dispatch(getDelegatedEmployees());
    //Scroll to the top of the page
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  if (data.isFetching || !defaultPlanYear) return <Loader />;

  const planYears =
    claimDashboard &&
    claimDashboard.plans &&
    claimDashboard.plans.map(item => ({
      value: item.planID,
      label: item.planName,
    }));

  const pendingClaims = claimDashboard && claimDashboard.pending;
  const processedClaims = claimDashboard && claimDashboard.processed;
  const filters = [
    { value: "All", label: intl.formatMessage(messages.claims.allClaimsDescription) },
    { value: "Pending", label: intl.formatMessage(messages.pendingClaimsDescription) },
    { value: "Processed", label: intl.formatMessage(messages.claims.processedClaimsDescription) },
  ];

  const delegators =
    data &&
    data.delegatedEmployees.map(item => ({
      value: `${item.employeeKey || null}`,
      label: item.delegatedEmployeeName,
    }));

  const delegatedEmployees = data.delegatedEmployees || []
  const selectedEmployee = delegatedEmployees.find(emp => String(emp.employeeKey) === String(selectedDelegator));

  const onPlanYearChanged = data => {
    setSelectedPlanYear(data.value);
    dispatch(getClaimDashboard(data.value, selectedDelegator !== "me" && selectedDelegator));
  };

  const onFilterChanged = data => {
    setSelectedFilter(data.value);
  };

  const onSelectedDelegatorChanged = data => {
    setSelectedDelegator(data.value);
    if (data.value !== "me") {
      dispatch(getClaimDashboard(selectedPlanYear, data.value));
    } else {
      dispatch(getClaimDashboard(selectedPlanYear));
    }
  };

  const action = {
    delete: (id, objectId) => {
      setModalFlag(true);
      setSelectedClaimId(id);
      setSelectedClaimObjectId(objectId);
    },
  };
  const onDeleteClaim = claimObjectId => {
    setModalFlag(false);
    dispatch(deleteClaim(claimObjectId, selectedDelegator !== "me" && selectedDelegator)).finally(
      () => {
        dispatch(getClaimDashboard());
      }
    );
  };

  const downloadReport = url => {
    dispatch(fetchPendingClaimsReport(url));
  };

  const openClaimExternal = (claimId, serviceProviderId, functionReferenceId) => {
    const text = intl.formatMessage({ ...messages.claims.externalClaimConfirmation });

    if (window.confirm(text)) {
      getShortLivedToken(dispatch)().then(response => {
        const { body: { slToken } = {} } = response;
        if (!slToken) {
          return;
        }
        const url = `/sso/sp/submit-claim?serviceProviderId=${serviceProviderId}&slToken=${slToken}&functionReferenceId=${functionReferenceId}&claimItemId=${claimId}`;
        const wnd = window.open(url, "_blank");
        wnd.focus();
      });
    }
  };

  return (
    <Fragment>
      {modalFlag && (
        <DeleteClaimDialog
          isOpen
          closeDeleteItemModal={() => {
            setModalFlag(false);
          }}
          eventHandler={onDeleteClaim}
          claimId={selectedClaimId}
          claimObjectId={selectedClaimObjectId}
          formLabel="Claim Form"
        />
      )}
      <Card>
        <CardSection>
          <div className="claim-submit-button-container">
            <div className="track-claim-labels">
              <table>
                <tr>
                  <td>
                    <div className="filter-label">
                      {intl.formatMessage(messages.claims.planYears)}
                    </div>
                  </td>
                  <td>
                    <Select
                      style={{ width: "200px" }}
                      value={selectedPlanYear || defaultPlanYear}
                      defaultValue={defaultPlanYear}
                      onChange={onPlanYearChanged}
                      isClearable={false}
                      options={planYears}
                    ></Select>
                  </td>
                </tr>
              </table>
            </div>
            <div className="track-claim-labels">
              <table>
                <tr>
                  <td>
                    <div className="filter-label">
                      {intl.formatMessage(messages.claims.filterDescription)}
                    </div>
                  </td>
                  <td style={{ width: "250px" }}>
                    <Select
                      value={selectedFilter}
                      defaultValue={selectedFilter}
                      onChange={onFilterChanged}
                      isClearable={false}
                      options={filters}
                    ></Select>
                  </td>
                </tr>
              </table>
            </div>
          </div>
          {delegators && delegators.length > 0 && (
            <div>
              <table>
                <tr>
                  <td style={{ width: "150px" }}>
                    <div>{intl.formatMessage(messages.claims.trackClaimFor)}</div>
                  </td>
                  <td style={{ width: "250px" }}>
                    <Select
                      options={delegators}
                      isClearable={false}
                      value={selectedDelegator}
                      onChange={onSelectedDelegatorChanged}
                    ></Select>
                  </td>
                </tr>
                <tr>
                  <td style={{ width: "150px" }}></td>
                  <td style={{ width: "200px" }}>
                    <span style={{ fontSize: "0.75rem" }}>
                      {intl.formatMessage(messages.claims.trackClaimForInstruction)}
                    </span>
                  </td>
                </tr>
              </table>
            </div>
          )}
        </CardSection>
        {selectedFilter !== "Processed" && (
          <div>
            <h3>{intl.formatMessage(messages.claims.pendingClaimsDescription)}</h3>
            {pendingClaims.ghsClaims.length > 0 ||
            pendingClaims.insuranceClaims.length > 0 ||
            pendingClaims.flexClaims.length > 0 ||
            pendingClaims.expenseClaims.length > 0 ? (
              <CardSection>
                {pendingClaims.ghsClaims && pendingClaims.ghsClaims.length > 0 && (
                  <div>
                    <h4>{intl.formatMessage(messages.claims.insuranceGHSClaimsDescription)}</h4>
                    <PendingClaimsTable
                      isInsuranceClaim="true"
                      claims={pendingClaims.ghsClaims}
                      columns={pendingClaims.pageFields}
                      action={action}
                      newUIFlag={true}
                      selectedDelegator={selectedDelegator}
                      type="ghsClaims"
                      employeeDelegator={selectedEmployee}
                      history={history}
                    />
                  </div>
                )}
                {pendingClaims.insuranceClaims && pendingClaims.insuranceClaims.length > 0 && (
                  <div>
                    <h4>
                      {intl.formatMessage(messages.claims.InsuranceOutpatientClaimsDescription)}
                    </h4>
                    <PendingClaimsTable
                      isInsuranceClaim="true"
                      claims={pendingClaims.insuranceClaims}
                      columns={pendingClaims.pageFields}
                      action={action}
                      newUIFlag={true}
                      selectedDelegator={selectedDelegator}
                      type="insuranceClaims"
                      employeeDelegator={selectedEmployee}
                      history={history}
                    />
                  </div>
                )}
                {pendingClaims.flexClaims && pendingClaims.flexClaims.length > 0 && (
                  <div>
                    {/* TEST */}
                    <h4> {intl.formatMessage(messages.claims.flexClaimsDescription)}</h4>
                    <PendingClaimsTable
                      claims={pendingClaims.flexClaims}
                      columns={pendingClaims.pageFields}
                      action={action}
                      newUIFlag={true}
                      selectedDelegator={selectedDelegator}
                      type="flexClaims"
                      employeeDelegator={selectedEmployee}
                      history={history}
                    />
                    {showEstimatedDateMessage(pendingClaims.pageFields) && (
                      <div
                        className="apl-claims__payment-description"
                        dangerouslySetInnerHTML={{
                          __html:
                            pendingClaims.disclaimer ||
                            intl.formatMessage(messages.claims.pendingClaimsEstimatedPaymentDate),
                        }}
                      />
                    )}
                  </div>
                )}

                {pendingClaims.expenseClaims && pendingClaims.expenseClaims.length > 0 && (
                  <div>
                    <h4> {intl.formatMessage(messages.claims.expenseClaimsDescription)}</h4>
                    <PendingClaimsTable
                      claims={pendingClaims.expenseClaims}
                      columns={pendingClaims.pageFields}
                      action={action}
                      newUIFlag={true}
                      selectedDelegator={selectedDelegator}
                      type="expenseClaims"
                      employeeDelegator={selectedEmployee}
                      history={history}
                    />
                    {showEstimatedDateMessage(pendingClaims.pageFields) && (
                      <div
                        className="apl-claims__payment-description"
                        dangerouslySetInnerHTML={{
                          __html:
                            pendingClaims.disclaimer ||
                            intl.formatMessage(messages.claims.pendingClaimsEstimatedPaymentDate),
                        }}
                      />
                    )}
                  </div>
                )}

                {claimDashboard.enableClaimsExportButton && pendingClaims.downloadPendingClaimsUrl && (
                  <Button
                    type="button"
                    style={{ marginTop: "20px" }}
                    onClick={() => downloadReport(pendingClaims.downloadPendingClaimsUrl)}
                    secondary
                  >
                    <FileDownloadIcon />
                    {intl.formatMessage(messages.exportAllDescription)}
                  </Button>
                )}
              </CardSection>
            ) : (
              <p>{intl.formatMessage(messages.claims.noRecordsFoundDescription)}</p>
            )}
          </div>
        )}
        {selectedFilter !== "Pending" && (
          <div>
            <h3> {intl.formatMessage(messages.claims.processedClaimsDescription)}</h3>
            {processedClaims.ghsClaims.length > 0 ||
            processedClaims.insuranceClaims.length > 0 ||
            processedClaims.flexClaims.length > 0 ||
            processedClaims.expenseClaims.length > 0 ? (
              <CardSection>
                {processedClaims.ghsClaims && processedClaims.ghsClaims.length > 0 && (
                  <div>
                    <h4>{intl.formatMessage(messages.claims.insuranceGHSClaimsDescription)}</h4>
                    <PendingClaimsTable
                      isInsuranceClaim="true"
                      claims={processedClaims.ghsClaims}
                      columns={processedClaims.pageFields}
                      newUIFlag={true}
                      selectedDelegator={selectedDelegator}
                      type="ghsClaims"
                      employeeDelegator={selectedEmployee}
                      history={history}
                    />
                  </div>
                )}
                {processedClaims.insuranceClaims && processedClaims.insuranceClaims.length > 0 && (
                  <div>
                    <h4>
                      {intl.formatMessage(messages.claims.InsuranceOutpatientClaimsDescription)}
                    </h4>
                    <PendingClaimsTable
                      isInsuranceClaim="true"
                      claims={processedClaims.insuranceClaims}
                      columns={processedClaims.pageFields}
                      newUIFlag={true}
                      selectedDelegator={selectedDelegator}
                      type="insuranceClaims"
                      employeeDelegator={selectedEmployee}
                      history={history}
                    />
                  </div>
                )}
                {processedClaims.flexClaims && processedClaims.flexClaims.length > 0 && (
                  <div>
                    <h4> {intl.formatMessage(messages.claims.flexClaimsDescription)}</h4>
                    <PendingClaimsTable
                      claims={processedClaims.flexClaims}
                      columns={processedClaims.pageFields}
                      newUIFlag={true}
                      selectedDelegator={selectedDelegator}
                      type="flexClaims"
                      employeeDelegator={selectedEmployee}
                      history={history}
                    />
                  </div>
                )}

                {processedClaims.expenseClaims && processedClaims.expenseClaims.length > 0 && (
                  <div>
                    <h4> {intl.formatMessage(messages.claims.expenseClaimsDescription)}</h4>
                    <PendingClaimsTable
                      claims={processedClaims.expenseClaims}
                      columns={processedClaims.pageFields}
                      newUIFlag={true}
                      selectedDelegator={selectedDelegator}
                      type="expenseClaims"
                      employeeDelegator={selectedEmployee}
                      history={history}
                    />
                  </div>
                )}
                {claimDashboard.enableClaimsExportButton &&
                  processedClaims.downloadHistoricalClaimsUrl && (
                    <Button
                      type="button"
                      style={{ marginTop: "20px" }}
                      onClick={() => downloadReport(processedClaims.downloadHistoricalClaimsUrl)}
                      secondary
                    >
                      <FileDownloadIcon />
                      {intl.formatMessage(messages.exportAllDescription)}
                    </Button>
                  )}
              </CardSection>
            ) : (
              <p>{intl.formatMessage(messages.claims.noRecordsFoundDescription)}</p>
            )}
          </div>
        )}
        <CardSection>
          {claimDashboard &&
            claimDashboard.claimClassifications &&
            claimDashboard.claimClassifications.length > 0 &&
            claimDashboard.claimClassifications[0].claimCategories &&
            claimDashboard.claimClassifications[0].claimCategories.map(category => {
              const { categoryName, children } = category;
              return (
                <div key={categoryName}>
                  <p>
                    <strong>
                      {intl.formatMessage(messages.claims.insurerClaimSubmittedStatusDescription)}
                    </strong>
                  </p>
                  <p>
                    <span style={{ fontSize: "0.875rem" }}>
                      {intl.formatMessage(messages.claims.claimSubmittedStatusNote)}
                    </span>
                  </p>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>
                          <FormattedMessage id={categoryName} defaultMessage={categoryName} />
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {children.map(item => (
                        <tr key={item.claimId}>
                          <td>
                            <Button
                              link
                              type="button"
                              onClick={() =>
                                openClaimExternal(item.claimId, item.serviceProviderId, "ClaimList")
                              }
                            >
                              <FormattedMessage
                                id={item.claimName}
                                defaultMessage={item.claimName}
                              />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })}
        </CardSection>
      </Card>
    </Fragment>
  );
};
TrackClaimsPage.propTypes = {
  intl: intlShape.isRequired,
  history: PropTypes.object,
};
export default injectIntl(TrackClaimsPage);



// WEBPACK FOOTER //
// ./components/claim/TrackClaimsPage.js